// Every tunable number in the proposed rules lives here, so a design change is a one-line edit.
// Changing anything that affects scores means bumping RULES_VERSION, because stored editions record
// the version they were published under. A board change can instead start from an edition number,
// as PATTERN_DAYS_FROM does, so earlier seeds still build the boards they were published with.

export const RULES_VERSION = 2

export const WORD_LENGTH = 5
export const ROWS = 6

/** Letter points are Scrabble's, so most players already know them. Rules v2 caps them (SCORING). */
export const LETTER_POINTS: Readonly<Record<string, number>> = {
  A: 1, E: 1, I: 1, O: 1, U: 1, L: 1, N: 1, S: 1, T: 1, R: 1,
  D: 2, G: 2,
  B: 3, C: 3, M: 3, P: 3,
  F: 4, H: 4, V: 4, W: 4, Y: 4,
  K: 5,
  J: 8, X: 8,
  Q: 10, Z: 10,
}

/**
 * How words score, by rules version. Every edition scores under the version it was published with,
 * so change scoring by adding a version here, never by editing one.
 * - 1: Scrabble points, and a gold letter adds its tier every time it appears.
 * - 2: letter points capped at 5 (J, Q, X and Z score 5), and a gold letter adds its tier once per
 *   word. Under v1 the same double-Z words (JAZZY, FRIZZ) and gold repeats (AHHHH, OHHHH) topped
 *   most boards; under v2 the day's gold letters decide more of the best words.
 */
export const SCORING: Readonly<Record<number, { readonly maxLetterPoints: number; readonly goldOncePerWord: boolean }>> = {
  1: { maxLetterPoints: 10, goldOncePerWord: false },
  2: { maxLetterPoints: 5, goldOncePerWord: true },
}

/** Burning all of these ends the rows early. Y is not a vowel here. */
export const VOWELS: readonly string[] = ['A', 'E', 'I', 'O', 'U']

/**
 * Burn tiles per row. The prototype shuffles [2, 2, 2, 1, 1, 1] across the rows; the Rules board
 * describes a fixed 2-1-2-1-2-1. For the fixed pattern use counts [2, 1, 2, 1, 2, 1] with
 * shuffle false. Two burns in a row are never side by side, so a row holds at most three.
 */
export const BURN_ROWS: { readonly counts: readonly number[]; readonly shuffle: boolean } = {
  counts: [2, 2, 2, 1, 1, 1],
  shuffle: true,
}

/**
 * From this edition on, burn layouts alternate by day: this edition and every second one after it
 * take one of BURN_PATTERNS, and the days between keep BURN_ROWS. Earlier editions all used
 * BURN_ROWS, so their seeds still build the boards they were published with.
 */
export const PATTERN_DAYS_FROM = 418

/**
 * From this pattern day on, shapes are dealt from a bag: every shape in BURN_PATTERNS comes up once
 * before any comes back, then the bag is shuffled again. The first round skips the shapes already
 * used since PATTERN_DAYS_FROM (picked at random, which put Horseshoe on No. 424 and No. 426). A
 * shape that comes back takes a placement it hasn't had yet (BURN_PATTERNS), so the burns never
 * repeat the time before and that day's words don't simply play again.
 */
export const PATTERN_BAG_FROM = 428

/** A new round's first shapes are never among the last ones of the round before, so a shape is never back within this many pattern days. */
export const PATTERN_SPACING = 7

/**
 * The shapes a pattern day's burns form across the board, top row first ('#' is a burn). Each is
 * mirrored left to right and flipped top to bottom; `upright` ones are never flipped. From
 * PATTERN_BAG_FROM a shape may also slide to any rows and columns it fits in, and `alt` is a second
 * drawing for a shape that would otherwise fit only one way, so every shape can come back changed.
 * Unlike BURN_ROWS, burns may sit side by side and a row may hold none or all five, so some days
 * a whole word goes into the ashes.
 */
export const BURN_PATTERNS: readonly {
  readonly name: string
  readonly rows: readonly string[]
  readonly upright?: boolean
  readonly alt?: readonly string[]
}[] = [
  { name: 'Cross', rows: ['.....', '..#..', '.###.', '.###.', '..#..', '.....'] },
  { name: 'Corners', rows: ['#...#', '.#.#.', '.....', '.....', '.#.#.', '#...#'], alt: ['##.##', '.....', '.....', '.....', '.....', '##.##'] },
  { name: 'Stairs', rows: ['....#', '...##', '..##.', '.##..', '##...', '#....'] },
  { name: 'Bars', rows: ['.....', '#####', '.....', '.....', '#####', '.....'] },
  { name: 'Horseshoe', rows: ['.....', '.#.#.', '.#.#.', '.#.#.', '.###.', '.....'] },
  { name: 'Diamond', rows: ['..#..', '.#.#.', '#...#', '#...#', '.#.#.', '..#..'], alt: ['..#..', '.#.#.', '#...#', '.#.#.', '..#..', '.....'] },
  { name: 'Ring', rows: ['.....', '.###.', '.#.#.', '.#.#.', '.###.', '.....'] },
  { name: 'X', rows: ['#...#', '.#.#.', '..#..', '.#.#.', '#...#', '.....'] },
  { name: 'Arrow', rows: ['..#..', '.###.', '#.#.#', '..#..', '..#..', '.....'] },
  { name: 'Heart', rows: ['.#.#.', '#.#.#', '#...#', '.#.#.', '..#..', '.....'], upright: true },
  { name: 'Tee', rows: ['#####', '..#..', '..#..', '..#..', '..#..', '.....'] },
  { name: 'Chevrons', rows: ['..#..', '.#.#.', '#...#', '..#..', '.#.#.', '#...#'] },
  { name: 'Smile', rows: ['.....', '.#.#.', '.#.#.', '.....', '#...#', '.###.'], upright: true },
  { name: 'Bolt', rows: ['...#.', '..#..', '.####', '...#.', '..#..', '.#...'] },
  { name: 'Blocks', rows: ['##...', '##...', '..#..', '...##', '...##', '.....'] },
  { name: 'Plus', rows: ['..#..', '..#..', '#####', '..#..', '..#..', '..#..'] },
  { name: 'Pyramid', rows: ['.....', '..#..', '.#.#.', '#...#', '#####', '.....'] },
  { name: 'Comb', rows: ['#####', '#.#.#', '.....', '.....', '.....', '.....'] },
  { name: 'Lollipop', rows: ['.###.', '.#.#.', '.###.', '..#..', '..#..', '.....'] },
  { name: 'Crescent', rows: ['.###.', '#....', '#....', '#....', '.###.', '.....'] },
  { name: 'Crown', rows: ['.....', '.....', '#.#.#', '#####', '.....', '.....'], upright: true },
  { name: 'Vee', rows: ['#...#', '#...#', '.#.#.', '.#.#.', '..#..', '.....'] },
  { name: 'Spine', rows: ['#.#.#', '..#..', '..#..', '..#..', '..#..', '#.#.#'], alt: ['.....', '#.#.#', '..#..', '..#..', '#.#.#', '.....'] },
  { name: 'Checkers', rows: ['.....', '#.#.#', '.#.#.', '#.#.#', '.....', '.....'] },
  { name: 'Goggles', rows: ['.....', '##.##', '##.##', '.....', '.....', '.....'] },
  { name: 'Ell', rows: ['#....', '#....', '#....', '#....', '#####', '.....'] },
  { name: 'Wave', rows: ['.....', '.#...', '###.#', '#.###', '...#.', '.....'] },
  { name: 'Bowtie', rows: ['.....', '#...#', '##.##', '#...#', '.....', '.....'] },
]

/** Today's gold letters: each adds its tier to a word's multiplier (how often depends on SCORING). */
export const GOLD_TIERS: readonly (1 | 2 | 3)[] = [1, 1, 1, 2, 2, 2, 3, 3]
export const MAX_GOLD_VOWELS = 2
export const RARE_LETTERS = 'JQXZ'
export const MAX_GOLD_RARE = 1

/** Edition No. 0. Makes 2026-10-18 No. 412. */
export const LAUNCH_DATE = '2025-09-01'
/** Editions turn over at midnight here (an open question in the proposal). */
export const EDITION_TIME_ZONE = 'America/Los_Angeles'

/**
 * Ranks, as a share of the best score the solver found for the edition. The steps are narrow where
 * finished games land: random everyday words that ignore gold make about 32% of the best, a word
 * from the better half of each row about 47-54%, and the best word each row (no lookahead) about
 * 82%. Until 2026-10-22 the steps were an even 0/15/30/45/60/75/90, which left the two lowest ranks
 * empty and put most players in Byline. Stored plays keep a rank_index, which the API re-ranks at
 * boot whenever these change (apps/api/src/services/ranks.ts).
 */
export const RANK_STEPS: readonly { readonly name: string; readonly pct: number }[] = [
  { name: 'Rough Draft', pct: 0 },
  { name: 'First Proof', pct: 0.25 },
  { name: 'Column', pct: 0.38 },
  { name: 'Byline', pct: 0.47 },
  { name: 'Headline', pct: 0.56 },
  { name: 'Front Page', pct: 0.68 },
  { name: 'Stop the Presses', pct: 0.88 },
]

/**
 * Beam-search settings. `beam` states survive each row and each expands into its `perState`
 * best-scoring legal words; states are ranked by score plus `vowelBias` per unburned vowel.
 * The browser prototype used beam 160 / perState 110; tools/solve.mjs used 600 / 250.
 */
export const SOLVER = { beam: 600, perState: 250, vowelBias: 12 } as const
export const SOLVER_QUICK = { beam: 160, perState: 110, vowelBias: 12 } as const

/**
 * Slow Burn, the second mode (src/slow-burn.ts): one flame tile on each of `rows` rows, a new
 * pattern every day from No. 1 on `launchDate`. `solverBeam` sizes the search for a day's best run
 * (beam 60 takes about 1.5 s in Node and reaches row 25 on every pattern tried).
 */
export const SLOW_BURN = { rows: 26, launchDate: '2026-10-24', solverBeam: 60 } as const

/**
 * Slow Burn's endgame fuse: once fewer than `wordsLeft` words fit, each row has `seconds` to be
 * played, timed from when the row came up, or the run ends (fuse_out) keeping its points. The
 * server allows `graceSeconds` more for the network. Early rows have no clock.
 */
export const SLOW_BURN_FUSE = { wordsLeft: 100, seconds: 60, graceSeconds: 5 } as const
